<template>
    <div>
        <template v-if="this.loading">
            <loading-spinner/>
        </template>
        <template v-else>
            <submission-wizard
                v-model="submission"
                :commitment="commitment"
                :currentStep="currentStep"
                :isConvertingFromManualSubmission="isConvertingFromManualSubmission || isConvertedFromManualSubmission"
                @submission:reset-structure="resetStructure"
                @submission:submit="submitQuote"
                @submission:save="saveSubmission"
                @submission:navigate-to="goToPage"
            />
        </template>
    </div>
</template>

<script>
    import SubmissionWizard from '~/components/Broker/SubmissionWizard';
    import submissionQuoteHelpers from "../../../mixins/submissionQuoteHelpers";

    export default {
        name: 'BrokerSubmissionEntry',
        props: {
            submissionCopy: {
                type: Object
            },
            commitment: {
                type: Object
            },
            isConvertingFromManualSubmission: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        mixins: [submissionQuoteHelpers],
        components: {
            SubmissionWizard
        },
        data() {
            return {
                loading: true,
                currentStep: 1,
                emptySubmission: { // Empty submission model, initialise props so that they are reactive for form validation
                    id: -1,
                    quote_type: null,
                    client_company_id: null,
                    placing_broker_company_id: undefined,
                    producing_broker_company_id: undefined,
                    captive_insurer_company_id: null,
                    facility_id: undefined,
                    category_id: null,
                    product_id: null,
                    country_id: null,
                    obligors: [
                        {
                            pivot: {
                                obligor_id: null,
                                sub_limit: null
                            }
                        }
                    ],
                    guarantors: [
                        {
                            country_id: null,
                            pivot: {
                                guarantor_id: null
                            }
                        }
                    ],
                    currency_id: null,
                    contract_value: '', // set as empty string as <x-currency-text-field> currently can't handle null
                    total_risk_period: null,
                    limit_of_liability: null,
                    indemnity: null,
                    gross_margin: null,
                    net_margin: null,
                    requested_risk_period: null,
                    product_data: {},
                    information_sections: [],
                    risk_type: 'submission',
                    manual_submission_tracker: null
                },
                submission: null,
            }
        },
        watch: {
            'currentStep'() {
                window.scroll({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }
        },
        async created() {
            this.submission = this.emptySubmission;
            this.currentStep = parseInt(this.$route.params.step) || 1;

            if (this.submissionCopy) {
                this.submission = _.cloneDeep(this.submissionCopy);
                this.submission.is_copy = true;

                if (this.isConvertingFromManualSubmission) {
                    this.submission.mst_id = this.submission.id;
                }

                this.submission.id = -1;
                this.removeCopiedRiskReferences();
                this.initDefaultObligorRisks();
            } else if (this.$route.params.id !== 'new') {
                this.submission = (await axios.get(this.$root.api(`broker/submission/${this.$route.params.id}`))).data.data;
            }

            this.loading = false;
        },
        computed: {
            isConvertedFromManualSubmission() {
                return this.submission.manual_submission_tracker !== null;
            }
        },
        methods: {
            resetStructure(risk_structure) {
                if (this.isConvertingFromManualSubmission) {
                    Vue.set(this.submission, 'risk_structure', risk_structure);

                    if (risk_structure === 'accepted_declaration') {
                        delete this.submission.entities;
                        Vue.set(this.submission, 'entity_id', this.submissionCopy.entities[0]?.id);
                    } else {
                        this.submission.entities = this.submissionCopy.entities;
                        Vue.set(this.submission, 'entity_id', null);
                    }

                } else {
                    this.submission = {
                        ...this.emptySubmission,
                        risk_structure,
                        obligors: [
                            {
                                pivot: {
                                    obligor_id: null,
                                    sub_limit: null
                                }
                            }
                        ],
                        guarantors: [
                            {
                                country_id: null,
                                pivot: {
                                    guarantor_id: null
                                }
                            }
                        ]
                    };
                }
            },
            async submitQuote() {
                this.loading = true;

                await axios.put(`/api/quote/${this.quote.id}/submit`, this.quote);

                this.$router.push('/dashboard');
            },
            async saveSubmission(step) {
                this.loading = true;
                if (this.submission.id === -1) {
                    this.processSaveSubmissionResponse(
                        await axios.post('/api/broker/submission', this.submission),
                        step
                    );
                } else {
                    this.processSaveSubmissionResponse(
                        await axios.put('/api/broker/submission/' + this.submission.id, this.submission),
                        step
                    );
                }
            },
            goToPage(step) {
                if (step === -1) {
                    this.$router.push('/agent/dashboard');
                } else {
                    this.currentStep = step;
                }
            },
            processSaveSubmissionResponse(response, step) {
                this.loading = false;

                if (step === 5) {
                    this.$router.push('/dashboard');
                    return;
                }

                this.submission = response.data.data;
                this.submission.insurers = this.submission.manual_submission_tracker?.insurers;

                // Replace url with plain JS so that vue-router does not reload the page.
                window.history.replaceState('', '', `/agent/risk/${this.submission.id}`);

                this.$route.params.id = this.submission.id;

                this.currentStep = step + 1;
            },
            initDefaultObligorRisks() {
                if (!this.submission.obligors.length) {
                    this.submission.obligors.push(this.getDefaultObligor());
                }
                if (!this.submission.guarantors.length) {
                    this.submission.guarantors.push(this.getDefaultGuarantor());
                }
            },
            removeCopiedRiskReferences() {
                const self = this;

                delete this.submission.offers;
                delete this.submission.updated_at;
                delete this.submission.created_at;
                delete this.submission.uuid;
                delete this.submission.owned_by_user_id;
                delete this.submission.created_by_user_id;
                //obligors
                if (this.submission.obligors.length) {
                    this.submission.obligors.forEach(function(oblogor) {
                        self.deleteProperty(oblogor.pivot, 'risk_id');
                    });
                }

                //guarantors
                if (this.submission.guarantors.length) {
                    this.submission.guarantors.forEach(function(guarantor) {
                        self.deleteProperty(guarantor.pivot, 'risk_id');
                    });
                }

                //additional information sections
                if (this.submission.information_sections.length) {
                    //this.submission.obligors.forEach());
                    this.submission.information_sections.forEach(function(informationSection) {
                        self.deleteProperty(informationSection, 'submission_id');
                        informationSection.permissions.forEach(function(permission) {
                            self.deleteProperty(permission, 'submission_id');
                            delete permission.information_section_id;
                        });
                    });
                }
            },
            deleteProperty(object, key) {
                delete object[key];

                if (object.id) { delete object.id; }
                if (object.created_at) { delete object.created_at; }
                if (object.updated_at) { delete object.updated_at; }
            }
        }
    }
</script>
